// Filters composable — chip-based filtering with suggestions
import { ref, computed } from 'vue'
import type { Entry, FilterChip, Service, Component } from '@/types'
import { typeLabels, severityLabels, statusLabels } from '@/i18n'
import { useI18n } from './useI18n'
import { useEvents } from './useEvents'
import { useAuth } from './useAuth'

const filterChips = ref<FilterChip[]>([])

export function useFilters() {
  const { lang, t, getEventTranslation } = useI18n()
  const { allEntries, servicesList, componentsList, findServiceByName, findComponentByName, getServiceLabel, getComponentLabel } = useEvents()
  const { isAuthenticated } = useAuth()

  const filteredEntries = computed(() => {
    return allEntries.value.filter((entry) => {
      // Filter out draft events for non-authenticated users
      if (!isAuthenticated.value && entry.state === 'draft') {
        return false
      }

      for (const chip of filterChips.value) {
        if (chip.kind === 'type' && entry.type !== chip.value) return false
        if (chip.kind === 'severity' && entry.severity !== chip.value) return false
        if (chip.kind === 'status' && entry.status !== chip.value) return false
        if (chip.kind === 'service' && entry.service !== chip.value) return false
        if (chip.kind === 'component' && entry.component !== chip.value) return false
        if (chip.kind === 'text') {
          const q = chip.value.toLowerCase()
          const title = getEventTranslation(entry.title).toLowerCase()
          const summary = getEventTranslation(entry.summary).toLowerCase()
          const details = entry.details ? getEventTranslation(entry.details).toLowerCase() : ''
          const serviceLabel = getServiceLabel(findServiceByName(entry.service), lang.value).toLowerCase()
          const componentLabel = entry.component
            ? getComponentLabel(findComponentByName(entry.component), lang.value).toLowerCase()
            : ''
          if (
            !title.includes(q) &&
            !summary.includes(q) &&
            !details.includes(q) &&
            !entry.service.toLowerCase().includes(q) &&
            !serviceLabel.includes(q) &&
            !(entry.component && entry.component.toLowerCase().includes(q)) &&
            !componentLabel.includes(q)
          ) {
            return false
          }
        }
      }
      return true
    })
  })

  function formatChipLabel(kind: string, value: string): string {
    if (kind === 'type') return typeLabels[lang.value]?.[value] || value
    if (kind === 'severity') return severityLabels[lang.value]?.[value] || value
    if (kind === 'status') return statusLabels[lang.value]?.[value] || value
    if (kind === 'service') {
      const svc = findServiceByName(value)
      return getServiceLabel(svc, lang.value) || value
    }
    if (kind === 'component') {
      const comp = findComponentByName(value)
      return getComponentLabel(comp, lang.value) || value
    }
    return value
  }

  function toggleChip(kind: FilterChip['kind'], value: string, label?: string) {
    const idx = filterChips.value.findIndex((c) => c.kind === kind && c.value === value)
    if (idx >= 0) {
      filterChips.value.splice(idx, 1)
    } else {
      filterChips.value.push({ kind, value, label: label || formatChipLabel(kind, value) })
    }
  }

  function removeChip(index: number) {
    filterChips.value.splice(index, 1)
  }

  function clearFilters() {
    filterChips.value = []
  }

  function buildSuggestions(query: string): FilterChip[] {
    const q = (query || '').trim().toLowerCase()
    if (!q) return []
    const list: FilterChip[] = []

    const add = (kind: FilterChip['kind'], value: string, label: string) => {
      if (filterChips.value.some((c) => c.kind === kind && c.value === value)) return
      list.push({ kind, value, label: label || value })
    }

    const matchesQuery = (label: string) => label.toLowerCase().includes(q)

    Object.keys(typeLabels.en).forEach((key) => {
      const label = typeLabels[lang.value]?.[key] || key
      if (matchesQuery(label) || key.includes(q)) add('type', key, label)
    })

    Object.keys(severityLabels.en).forEach((key) => {
      const label = severityLabels[lang.value]?.[key] || key
      if (matchesQuery(label) || key.includes(q)) add('severity', key, label)
    })

    Object.keys(statusLabels.en).forEach((key) => {
      const label = statusLabels[lang.value]?.[key] || key
      if (matchesQuery(label) || key.includes(q)) add('status', key, label)
    })

    servicesList.value.forEach((service) => {
      const label = getServiceLabel(service, lang.value)
      if (matchesQuery(label) || matchesQuery(service.name)) add('service', service.name, label)
    })

    componentsList.value.forEach((component) => {
      const label = getComponentLabel(component, lang.value)
      if (matchesQuery(label) || matchesQuery(component.name)) add('component', component.name, label)
    })

    if (q) {
      add('text', q, `${t('filter-placeholder')}: ${query.trim()}`)
    }

    return list.slice(0, 15)
  }

  function buildChipFromInput(rawValue: string): FilterChip | null {
    const value = (rawValue || '').trim()
    if (!value) return null
    const lower = value.toLowerCase()

    const matchFromLabels = (labelMap: Record<string, Record<string, string>>) =>
      Object.keys(labelMap.en).find((key) => {
        const en = labelMap.en[key].toLowerCase()
        const de = labelMap.de?.[key]?.toLowerCase() || ''
        return key === lower || en === lower || de === lower
      })

    const typeMatch = matchFromLabels(typeLabels)
    if (typeMatch) return { kind: 'type', value: typeMatch, label: formatChipLabel('type', typeMatch) }

    const severityMatch = matchFromLabels(severityLabels)
    if (severityMatch) return { kind: 'severity', value: severityMatch, label: formatChipLabel('severity', severityMatch) }

    const statusMatch = matchFromLabels(statusLabels)
    if (statusMatch) return { kind: 'status', value: statusMatch, label: formatChipLabel('status', statusMatch) }

    const serviceMatchesInput = (service: Service, lower: string) => {
      const names = [service.name, service.translations?.en?.name, service.translations?.de?.name].filter(Boolean) as string[]
      return names.some((n) => n.toLowerCase() === lower)
    }

    const componentMatchesInput = (component: Component, lower: string) => {
      const names = [component.name, component.translations?.en?.name, component.translations?.de?.name].filter(Boolean) as string[]
      return names.some((n) => n.toLowerCase() === lower)
    }

    const serviceMatch = servicesList.value.find((s) => serviceMatchesInput(s, lower))
    if (serviceMatch) return { kind: 'service', value: serviceMatch.name, label: getServiceLabel(serviceMatch, lang.value) }

    const componentMatch = componentsList.value.find((c) => componentMatchesInput(c, lower))
    if (componentMatch) return { kind: 'component', value: componentMatch.name, label: getComponentLabel(componentMatch, lang.value) }

    return { kind: 'text', value, label: value }
  }

  return {
    filterChips,
    filteredEntries,
    formatChipLabel,
    toggleChip,
    removeChip,
    clearFilters,
    buildSuggestions,
    buildChipFromInput,
  }
}
